import { useCallback, useRef } from "react";
import type { NotifiableError } from "@bugsnag/core/types/common";
import { useRequest } from "hooks/useRequest";
import { bugsnagClient } from "../../bugsnag";
import { diffTeam, fetchProjectTeamData } from "./utils";
import type {
  ProjectMemberRole,
  ProjectTeamChanges,
  ProjectTeamMember,
  UseProjectTeamArgs,
  UseProjectTeamResult,
} from "./types";

const EMPTY_ARRAY: never[] = [];

export const useProjectTeam = ({
  projectId,
  canManageTeam,
}: UseProjectTeamArgs): UseProjectTeamResult => {
  // Server snapshot to diff against; the request data holds the staged copy.
  const originalTeamRef = useRef<ProjectTeamMember[]>([]);

  const {
    data: teamData,
    mutateData,
    isLoading,
  } = useRequest(
    async () => {
      try {
        const data = await fetchProjectTeamData(projectId);
        originalTeamRef.current = data.teamMembers;
        return data;
      } catch (error) {
        bugsnagClient.notify(error as NotifiableError);
        return { teamMembers: [], availableMembers: [] };
      }
    },
    canManageTeam && projectId ? { defaultParams: [] } : { manual: true },
  );

  const teamMembers = teamData?.teamMembers ?? EMPTY_ARRAY;
  const availableMembers = teamData?.availableMembers ?? EMPTY_ARRAY;

  const addMember = useCallback(
    (memberUserId: number, role: ProjectMemberRole) => {
      mutateData({
        updater: (data) => {
          const memberToAdd = data.availableMembers.find(
            (member) => member.id === memberUserId,
          );
          if (!memberToAdd) return data;
          return {
            teamMembers: [
              ...data.teamMembers,
              {
                id: memberToAdd.id,
                name: memberToAdd.name,
                email: memberToAdd.email,
                projectRole: role,
                organizationRole: memberToAdd.organizationRole,
              },
            ],
            availableMembers: data.availableMembers.filter(
              (member) => member.id !== memberUserId,
            ),
          };
        },
      });
    },
    [mutateData],
  );

  const changeRole = useCallback(
    (memberUserId: number, role: ProjectMemberRole) => {
      mutateData({
        updater: (data) => ({
          ...data,
          teamMembers: data.teamMembers.map((member) =>
            member.id === memberUserId
              ? { ...member, projectRole: role }
              : member,
          ),
        }),
      });
    },
    [mutateData],
  );

  const removeMember = useCallback(
    (memberUserId: number) => {
      mutateData({
        updater: (data) => {
          const memberToRemove = data.teamMembers.find(
            (member) => member.id === memberUserId,
          );
          if (!memberToRemove) return data;
          return {
            teamMembers: data.teamMembers.filter(
              (member) => member.id !== memberUserId,
            ),
            availableMembers: [
              ...data.availableMembers,
              {
                id: memberToRemove.id,
                name: memberToRemove.name,
                email: memberToRemove.email,
                projectRole: memberToRemove.projectRole,
                organizationRole: memberToRemove.organizationRole,
              },
            ],
          };
        },
      });
    },
    [mutateData],
  );

  const getTeamChanges = useCallback(
    (): ProjectTeamChanges | null =>
      diffTeam(originalTeamRef.current, teamMembers),
    [teamMembers],
  );

  return {
    isLoading,
    teamMembers,
    availableMembers,
    addMember,
    changeRole,
    removeMember,
    getTeamChanges,
  };
};
